import type { LucideIcon } from "lucide-react";
import { AlertCircle, ArrowRight, ChevronDown } from "lucide-react";
import { Component, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { Link } from "../../router.tsx";

/** What a section's data is doing: loading keeps its height, an error offers Retry. */
export interface SectionState {
  /** Still loading its first data. */
  pending: boolean;
  /** Its first load failed (a failed background refetch keeps what is shown). */
  error: unknown;
  retry: () => void;
  /** Rows the loading placeholder holds, so the layout does not jump (default 3). */
  rows?: number;
}

export type SectionQuery = {
  isPending: boolean;
  isError: boolean;
  data: unknown;
  error: unknown;
  refetch: () => unknown;
};

/** The section's state from its queries: the first load pending or failed, Retry refetches. */
export function stateOf(queries: readonly SectionQuery[], rows: number) {
  const failed = queries.find((q) => q.isError && q.data === undefined);
  return {
    pending: queries.some((q) => q.isPending),
    error: failed?.error ?? null,
    rows,
    retry: () => {
      for (const q of queries) {
        if (q.isError) {
          void q.refetch();
        }
      }
    },
  };
}

/**
 * One section of Overview (design D2): a quiet 13px heading with an optional count and a link to
 * its tab, then one hairline container of rows or figure lines. Loading keeps the container's
 * height with a quiet placeholder (never sample rows); an error shows inline with Retry, inside
 * this section only, and a render error is caught here so the other sections still render.
 */
export function HomeSection(props: {
  id: string;
  title: string;
  /** The total beside the heading (Needs you); "50+" when it is a lower bound. */
  count?: number | string;
  countLabel?: string;
  /** "Open" or "Show all": the tab holding the full content. */
  link?: { href: string; label: string };
  state?: SectionState;
  /** A line between the heading and the container (filter chips). */
  toolbar?: ReactNode;
  /** One 12px muted line under the container. */
  foot?: ReactNode;
  /** Without the container (Getting started holds its own anatomy). */
  bare?: boolean;
  /**
   * A figure section's one-line summary ("48 pages · 2 waiting"): below 768px the container
   * collapses to it, a disclosure that expands the figures in place (design D3).
   */
  summaryLine?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const bodyId = useId();
  const { state } = props;
  const phone = !useMediaQuery("(min-width: 768px)");
  const [expanded, setExpanded] = useState(false);
  const collapsible =
    props.summaryLine !== undefined && phone && state?.pending !== true && !state?.error;
  const body =
    state?.pending === true ? (
      <Placeholder rows={state.rows ?? 3} label={t("projectOverview.loading")} />
    ) : state?.error ? (
      <SectionError error={state.error} retry={state.retry} />
    ) : (
      props.children
    );
  return (
    <section
      aria-labelledby={headingId}
      data-home-section={props.id}
      className="flex flex-col gap-2"
    >
      <div className="flex min-h-6 items-baseline justify-between gap-3">
        <h2 id={headingId} className="text-[13px] font-semibold text-fg">
          {props.title}
          {props.count !== undefined ? (
            <>
              <span aria-hidden="true" className="ml-1.5 font-medium text-fg-muted tabular-nums">
                {props.count}
              </span>
              {props.countLabel ? <span className="sr-only">, {props.countLabel}</span> : null}
            </>
          ) : null}
        </h2>
        {props.link ? (
          <Link
            href={props.link.href}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-accent-text underline-offset-4 hover:underline"
          >
            {props.link.label}
            <ArrowRight aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
          </Link>
        ) : null}
      </div>
      {props.toolbar}
      <SectionBoundary>
        {props.bare ? (
          body
        ) : collapsible ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface-raised">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={bodyId}
              onClick={() => setExpanded((open) => !open)}
              className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-fg-secondary transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
            >
              <span className="min-w-0 break-words tabular-nums">{props.summaryLine}</span>
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "size-4 shrink-0 text-fg-muted transition-transform duration-(--duration-fast)",
                  expanded && "rotate-180",
                )}
                strokeWidth={1.75}
              />
            </button>
            <div id={bodyId} hidden={!expanded} className="border-t border-line">
              {body}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-line bg-surface-raised">
            {body}
          </div>
        )}
      </SectionBoundary>
      {props.foot && !state?.pending && !state?.error && (!collapsible || expanded) ? (
        <p className="text-xs text-fg-muted">{props.foot}</p>
      ) : null}
    </section>
  );
}

function Placeholder(props: { rows: number; label: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={props.label} className="divide-y divide-line">
      {Array.from({ length: props.rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholder lines
        <div key={i} className="px-4 py-3">
          <div className="h-4 w-2/5 rounded bg-surface-sunken" />
        </div>
      ))}
    </div>
  );
}

/** The section's own inline error: one danger line and Retry. */
function SectionError(props: { error: unknown; retry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <p className="inline-flex items-center gap-1.5 text-sm text-danger-fg">
        <AlertCircle aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
        {t("projectOverview.loadError")}
      </p>
      <Button size="sm" variant="outline" onClick={props.retry}>
        {t("app.retry")}
      </Button>
    </div>
  );
}

/** Catches a section's render error, so one broken section never blanks Overview. */
class SectionBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? (
      <div className="overflow-hidden rounded-lg border border-line bg-surface-raised">
        <SectionError retry={() => this.setState({ failed: false })} error={null} />
      </div>
    ) : (
      this.props.children
    );
  }
}

/** A list row: a 16px icon, a 14px medium title (the link), a 12px muted line, a time. */
export function HomeRow(props: {
  icon: LucideIcon;
  href: string;
  title: ReactNode;
  /** The muted line's parts, joined by middots. */
  meta?: readonly ReactNode[];
  /** The tabular time at the end. */
  at?: string;
  atLabel?: string;
  /** A state on the title line (overdue): its words and colour. */
  tone?: "warning" | "danger";
  className?: string;
}) {
  const Icon = props.icon;
  const meta = (props.meta ?? []).filter((part) => part !== null && part !== false && part !== "");
  return (
    <li
      className={cn(
        "relative flex items-start gap-3 px-4 py-3 transition-colors duration-(--duration-fast) focus-within:bg-surface-hover hover:bg-surface-hover",
        props.className,
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          "mt-0.5 size-4 shrink-0",
          props.tone === "warning"
            ? "text-warning-fg"
            : props.tone === "danger"
              ? "text-danger-fg"
              : "text-fg-muted",
        )}
        strokeWidth={1.75}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Link
          href={props.href}
          className="text-sm font-medium break-words text-fg after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
        >
          {props.title}
        </Link>
        {meta.length > 0 ? (
          <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
            {meta.map((part, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: positional parts of one line
              <span key={i} className="inline-flex items-center gap-x-1.5">
                {i > 0 ? <span aria-hidden="true">·</span> : null}
                {part}
              </span>
            ))}
          </p>
        ) : null}
      </div>
      {props.at ? (
        <time
          dateTime={props.at}
          className="mt-0.5 shrink-0 text-xs text-fg-muted tabular-nums"
          title={props.atLabel}
        >
          {props.atLabel}
        </time>
      ) : null}
    </li>
  );
}

/** A list of rows in the section's container. */
export function HomeRows(props: { children: ReactNode; label?: string; labelledBy?: string }) {
  return (
    <ul
      aria-label={props.label}
      aria-labelledby={props.labelledBy}
      className="divide-y divide-line"
    >
      {props.children}
    </ul>
  );
}

/** The one line a list shows when it has nothing to list. */
export function HomeEmptyLine(props: { children: ReactNode }) {
  return <p className="px-4 py-3 text-sm text-fg-secondary">{props.children}</p>;
}

/**
 * A figure line (design D2): the label left in secondary text, the tabular number right in
 * medium weight; the whole line is a link only when it leads somewhere. A state figure (failed)
 * adds the state's dot and colour on the number, and its word stays in the label.
 */
export function FigureLine(props: {
  label: string;
  value: ReactNode;
  href?: string | undefined;
  state?: "danger" | "warning" | undefined;
}) {
  const content = (
    <>
      <span className="min-w-0 text-sm text-fg-secondary">{props.label}</span>
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 text-sm font-medium tabular-nums",
          props.state === "danger"
            ? "text-danger-fg"
            : props.state === "warning"
              ? "text-warning-fg"
              : "text-fg",
        )}
      >
        {props.state ? (
          <span
            aria-hidden="true"
            className={cn(
              "size-1.5 rounded-full",
              props.state === "danger" ? "bg-danger-border" : "bg-warning-border",
            )}
          />
        ) : null}
        {props.value}
      </span>
    </>
  );
  const line = "flex items-center justify-between gap-3 px-4 py-2.5";
  return (
    <li>
      {props.href ? (
        <Link
          href={props.href}
          className={cn(
            line,
            "transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none",
          )}
        >
          {content}
        </Link>
      ) : (
        <div className={line}>{content}</div>
      )}
    </li>
  );
}
